import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, InputNumber } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { useUpdateInventoryControlMutation } from '@/services/api/inventory/inventoryApi';
import { CustomModal } from '@/components';

const { TextArea } = Input;

/**
 * Edit Inventory Modal Component
 * Handles updating stock quantities for inventory items
 */
const EditInventoryModal = ({ 
  visible, 
  onCancel, 
  onSuccess,
  eventData,
  userId = 1 // Pass the current user ID from parent component or auth context
}) => {
  const [form] = Form.useForm();
  const [quantity, setQuantity] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [updateInventoryControl, { isLoading }] = useUpdateInventoryControlMutation();

  // Populate form when eventData changes
  useEffect(() => {
    if (eventData && visible) {
      setQuantity(eventData.current_qty || 0);
      form.setFieldsValue({
        quantity: eventData.current_qty || 0,
        reason: ''
      });
    }
  }, [eventData, visible, form]);

  // Handle quantity increment
  const handleIncrement = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    form.setFieldValue('quantity', newQuantity);
  };

  // Handle quantity decrement
  const handleDecrement = () => {
    const newQuantity = Math.max(0, quantity - 1);
    setQuantity(newQuantity);
    form.setFieldValue('quantity', newQuantity);
  };

  // Handle manual quantity change
 const handleQuantityChange = (value) => {
  const newValue = value ?? 0; // Use nullish coalescing
  setQuantity(newValue);
  form.setFieldValue('quantity', newValue);
};
  // Handle form submission
  const handleSubmit = async () => {
      // console.log(eventData?.product_id, "Event Data for Update");

    try {
      const values = await form.validateFields();
  if (!eventData?.product_id) {
  message.error('Product ID is missing');
  return;
}

      setIsSubmitting(true);
      const payloadData = {
        id: eventData.product_id, // The product_id
        requestData: {
          current_qty: values.quantity, // The current stock
          restocked_by: userId, // User who is updating
          reason: values.reason || '' // Reason for update
        }
      };

      await updateInventoryControl(payloadData).unwrap();
      
      message.success('Stock updated successfully!');
      form.resetFields();
      setQuantity(0);
      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      if (error.errorFields) {
        // Form validation error
        message.error('Please fill in all required fields');
      } else {
        console.error('Error updating stock:', error);
        message.error(error?.data?.message || 'Failed to update stock');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleCancel = () => {
    form.resetFields();
    setQuantity(0);
    onCancel();
  };

  if (!eventData) return null;

  return (
    <CustomModal
      isOpen={visible}
      onClose={handleCancel}
      title={`Update Stock for ${eventData.product_name || 'Item'}`}
      primaryActionText="Save"
      onPrimaryAction={handleSubmit}
      secondaryActionText="Cancel"
      onSecondaryAction={handleCancel}
      primaryActionDisabled={isSubmitting || isLoading}
      loading={isSubmitting || isLoading}
      width={400}
      isCenter={true}
      maskClosable={false}
    >
      <div className="px-6 py-4">
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          {/* Available Quantity Display */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Quantity:
            </label>
            <div className="text-2xl font-semibold text-gray-900">
              {eventData.current_qty || 0} Litres
            </div>
          </div>

          {/* Update Quantity Input */}
<Form.Item
  label={
    <span className="text-sm font-medium text-gray-700">
      Update<span className="text-red-500">*</span>
    </span>
  }
  name="quantity"
  rules={[
    { required: true, message: 'Please enter quantity' },
    { 
      validator: (_, value) => {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 0) {
          return Promise.reject(new Error('Quantity must be positive'));
        }
        return Promise.resolve();
      }
    }
  ]}
  validateTrigger={['onChange', 'onBlur']}
>
  <div className="flex items-center gap-2">
    <Button
      icon={<MinusOutlined />}
      onClick={handleDecrement}
      disabled={quantity <= 0}
      className="w-10 h-10 flex items-center justify-center border-gray-300 rounded-lg"
    />
    <InputNumber
      value={quantity}
      onChange={handleQuantityChange}
      min={0}
      className="flex-1 text-center"
      size="large"
      controls={false}
      parser={(value) => {
        const parsed = value?.replace(/[^\d]/g, '');
        return parsed ? Number(parsed) : 0;
      }}
      style={{ 
        width: '100%',
        textAlign: 'center',
        fontSize: '16px'
      }}
    />
    <Button
      icon={<PlusOutlined />}
      onClick={handleIncrement}
      className="w-10 h-10 flex items-center justify-center border-gray-300 rounded-lg"
    />
  </div>
</Form.Item>
        </Form>
      </div>
    </CustomModal>
  );
};

export default EditInventoryModal;